import { API, apiUrl } from '@/config';
import type { ActionLogActor, ActionLogFilters, ActionLogQuery, ActionLogResponse } from '@/types';
import { api, ApiError, refreshOnce } from './client';
import { getAccessToken } from './tokens';

function toParams(query: ActionLogQuery): string {
  const params = new URLSearchParams();
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.offset !== undefined) params.set('offset', String(query.offset));
  if (query.search) params.set('search', query.search);
  if (query.eventType) params.set('eventType', query.eventType);
  if (query.actor) params.set('actor', query.actor);
  if (query.period) params.set('period', query.period);

  return params.size > 0 ? `?${params.toString()}` : '';
}

/**
 * The estate-wide action log.
 *
 * Bypasses the shared `api.get` helper, same reason as `customers.service`'s
 * own list call: that helper unwraps the `{ data, meta }` envelope down to
 * just `data`, which is exactly the pagination metadata this table needs to
 * render its page numbers.
 */
async function list(query: ActionLogQuery, signal?: AbortSignal): Promise<ActionLogResponse> {
  const token = getAccessToken();

  const response = await fetch(apiUrl(`${API.endpoints.actionLog}${toParams(query)}`), {
    headers: token ? { authorization: `Bearer ${token}` } : {},
    credentials: 'include',
    signal: signal ?? AbortSignal.timeout(API.timeoutMs),
  }).catch(() => null);

  if (!response) throw new ApiError(0, null, 'Could not reach the server.');
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as null;
    throw new ApiError(response.status, body, 'Could not load the action log.');
  }

  return (await response.json()) as ActionLogResponse;
}

function actors(signal?: AbortSignal): Promise<ActionLogActor[]> {
  return api.get<ActionLogActor[]>(API.endpoints.actionLogActors, signal ? { signal } : {});
}

/** A whole year of events can take a while to stream; the usual request
 *  timeout would cut a large export off halfway. */
const EXPORT_TIMEOUT_MS = 120_000;

function filenameFrom(response: Response): string {
  const header = response.headers.get('content-disposition') ?? '';
  return /filename="([^"]+)"/.exec(header)?.[1] ?? 'action-log.csv';
}

/**
 * Every row matching the filters, as the CSV the server builds - not the page
 * on screen. Fetched rather than linked to because the endpoint needs the
 * bearer token, which a plain `<a href>` cannot send.
 */
async function exportCsv(filters: ActionLogFilters): Promise<{ blob: Blob; filename: string }> {
  const request = () => {
    const token = getAccessToken();
    return fetch(apiUrl(`${API.endpoints.actionLogExport}${toParams(filters)}`), {
      headers: token ? { authorization: `Bearer ${token}` } : {},
      credentials: 'include',
      signal: AbortSignal.timeout(EXPORT_TIMEOUT_MS),
    }).catch(() => null);
  };

  let response = await request();
  // An access token that lapsed while somebody was reading the table should
  // not fail the export; one refresh, the same as every other call gets.
  if (response?.status === 401 && (await refreshOnce())) response = await request();

  if (!response) throw new ApiError(0, null, 'Could not reach the server.');
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as null;
    throw new ApiError(response.status, body, 'Could not export the action log.');
  }

  return { blob: await response.blob(), filename: filenameFrom(response) };
}

export const actionLogService = { list, actors, exportCsv };
